import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { tapClickProps } from '../../utils/tapFix';
import tabletLogo from '../../assets/tablet_logo.png';

// 하단바 액션 아이콘 (디자인 제공 SVG)
const StaffCallIcon = () => (
  <svg className="tb-action-icon" width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.9998 3.96667C14.8582 3.96667 12.8043 4.81743 11.29 6.33179C9.7756 7.84614 8.92485 9.90005 8.92485 12.0417C8.92485 17.85 6.65818 19.8333 5.80818 20.9667C5.24151 21.675 5.80818 22.6667 6.65818 22.6667H27.3415C28.1915 22.6667 28.7582 21.675 28.1915 20.9667C27.1998 19.8333 25.0748 17.85 25.0748 12.0417C25.0748 9.90005 24.2241 7.84614 22.7097 6.33179C21.1954 4.81743 19.1415 3.96667 16.9998 3.96667Z" fill="#2A2723" />
    <path d="M13.8833 26.9166C14.0869 27.5845 14.4997 28.1693 15.0609 28.5848C15.622 29.0003 16.3017 29.2246 17 29.2246C17.6982 29.2246 18.378 29.0003 18.9391 28.5848C19.5002 28.1693 19.913 27.5845 20.1166 26.9166" stroke="#2A2723" strokeWidth="2.83333" strokeLinecap="round" />
  </svg>
);

const OrderHistoryIcon = () => (
  <svg className="tb-action-icon" width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.2333 4.81665H10.7666C8.41939 4.81665 6.5166 6.71944 6.5166 9.06665V24.9333C6.5166 27.2805 8.41939 29.1833 10.7666 29.1833H23.2333C25.5805 29.1833 27.4833 27.2805 27.4833 24.9333V9.06665C27.4833 6.71944 25.5805 4.81665 23.2333 4.81665Z" stroke="#2A2723" strokeWidth="2.83333" />
    <path d="M11.8999 12.75H22.0999M11.8999 18.4167H22.0999M11.8999 24.0833H17.8499" stroke="#2A2723" strokeWidth="2.83333" strokeLinecap="round" />
  </svg>
);

/**
 * 가로형 태블릿 전용 주문 메인 화면.
 *
 * 렌더 방식: 피그마 아트보드(2000x1200)를 "폭 기준"으로 zoom 스케일하고,
 * 높이는 (뷰포트높이 / zoom) 으로 되돌려 세로를 꽉 채운다.
 * → 가로는 시안과 픽셀 단위로 동일하고, 남는 세로는 메뉴 목록이 흡수해 행 수만 달라진다.
 * (public/review-countdown.html 의 applyZoom 과 같은 기법)
 *
 * 주의: 이 프로젝트는 styled-jsx 가 실제로 컴파일되지 않아 모든 CSS 가 전역이다.
 *      orderPage 의 1400줄 전역 블록과 충돌하지 않도록 모든 클래스에 `tb-` 접두사를 쓴다.
 */

const ARTBOARD_WIDTH = 2000;

// orderPage 의 languages 배열은 name 이 'KO'/'EN' 같은 축약형이라 시안 표기와 다르다.
const LANG_LABELS = { ko: '한국어', en: 'English', ja: '日本語', zh: '中文' };

const TabletOrderView = ({
  // 사이드바
  storeName,
  tableNumber,
  categories = [],
  categoryCodeMapping = {},
  categoryTranslations = {},
  activeCategoryIndex = 0,
  onSelectCategory,
  // 상단바 / 언어
  t = {},
  selectedLanguage = 'ko',
  languages = [],
  onSelectLanguage,
  // 메뉴
  menuData = {},
  translatedMenus = {},
  menutagImages = {},
  calculateTimeRemaining,
  onMenuClick,
  // 하단바
  cartCount = 0,
  cartTotal = 0,
  onOpenCart,
  onOpenOrderHistory,
  onOpenStaffCall,
  // 토스트
  isToastVisible,
  toastMessage,
}) => {
  const stageRef = useRef(null);
  const listRef = useRef(null);
  const [isLangOpen, setIsLangOpen] = useState(false);
  // 커스텀 스크롤 인디케이터 — 네이티브 스크롤바는 가장자리에 붙고 상하 여백을 줄 수 없어
  // 시안(우측 18px 안쪽, 상하 32px 여백, 폭 6px)을 재현하려면 직접 그려야 한다.
  const [thumb, setThumb] = useState({ visible: false, top: 0, height: 0 });

  const updateThumb = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1) {
      setThumb(prev => (prev.visible ? { ...prev, visible: false } : prev));
      return;
    }
    const trackHeight = Math.max(0, clientHeight - 64); // 상하 32px 여백
    const height = Math.max(60, Math.round((trackHeight * clientHeight) / scrollHeight));
    const maxTop = Math.max(0, trackHeight - height);
    const top = Math.round((maxTop * scrollTop) / (scrollHeight - clientHeight));
    setThumb(prev =>
      prev.visible && prev.top === top && prev.height === height
        ? prev
        : { visible: true, top, height }
    );
  }, []);

  // 아트보드 스케일러 — ref 기반 + null 가드(회전으로 언마운트된 직후 resize 방어)
  useEffect(() => {
    const timers = [];
    let rafId = 0;

    const applyScale = () => {
      const stage = stageRef.current;
      if (!stage) return;
      const el = document.documentElement;
      const w = (el && el.clientWidth) || window.innerWidth || 0;
      const h = (el && el.clientHeight) || window.innerHeight || 0;
      if (!w || !h) return;

      const z = w / ARTBOARD_WIDTH;
      stage.style.zoom = z;
      // 뷰포트 단위는 zoom 으로 나뉘지 않으므로 높이를 직접 되돌려준다.
      stage.style.height = (h / z) + 'px';
    };

    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(applyScale);
    };
    // iOS 는 회전 직후 몇 프레임 동안 이전 치수를 보고한다.
    const handleOrientation = () => {
      applyScale();
      timers.push(setTimeout(applyScale, 100));
      timers.push(setTimeout(applyScale, 350));
    };

    applyScale();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientation);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      timers.forEach(clearTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientation);
    };
  }, []);

  // Noto Sans KR — 태블릿 뷰가 떠 있을 때만 로드 (모바일 사용자는 내려받지 않음)
  useEffect(() => {
    const ID = 'tb-noto-sans-kr';
    if (document.getElementById(ID)) return;
    const link = document.createElement('link');
    link.id = ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700;900&display=swap';
    document.head.appendChild(link);
    // 회전으로 모바일↔태블릿을 오갈 때 재요청하지 않도록 언마운트해도 제거하지 않는다
  }, []);

  // 카테고리 표시명 (orderPage 의 해석 규칙과 동일)
  const categoryLabel = (category) => {
    const code = categoryCodeMapping[category];
    return (code && categoryTranslations[code]?.[selectedLanguage]) || category;
  };

  const currentCategory = categories[activeCategoryIndex] || categories[0] || '';
  const items = (menuData && menuData[currentCategory]) || [];

  // 카테고리를 바꾸면 목록을 맨 위로 되돌린다
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    updateThumb();
  }, [activeCategoryIndex, updateThumb]);

  // 목록 내용 · 창 크기가 바뀌면 인디케이터 재계산
  useEffect(() => {
    updateThumb();
    window.addEventListener('resize', updateThumb);
    return () => window.removeEventListener('resize', updateThumb);
  }, [updateThumb, items.length]);

  const itemCountLabel = {
    ko: `${items.length}개`,
    en: `${items.length} items`,
    ja: `${items.length}種`,
    zh: `${items.length}种`,
  }[selectedLanguage] || `${items.length}개`;

  const formatPrice = (item) => {
    const raw = item.basePrice || item.price;
    const num = typeof raw === 'number'
      ? raw
      : parseInt(String(raw || '0').replace(/[^0-9]/g, '')) || 0;
    return selectedLanguage === 'ko'
      ? `${num.toLocaleString()}원`
      : `₩${num.toLocaleString()}`;
  };

  // 태그 배지 (orderPage 와 동일한 종류/정렬)
  const renderTags = (item) => {
    const tags = [];
    if (item.is_popular || item.tags?.includes('popular')) tags.push('popular');
    if (item.is_recommend || item.tags?.includes('recommend')) tags.push('recommend');
    if (item.is_new || item.tags?.includes('new')) tags.push('new');
    if (item.is_most_ordered) tags.push('mostOrdered');

    return tags
      .sort((a, b) => {
        const order = { popular: 0, recommend: 1, new: 2, mostOrdered: 3 };
        return order[a] - order[b];
      })
      .map((tag, i) => {
        if (tag === 'mostOrdered') {
          const label = {
            ko: '많이 골랐어요!',
            en: 'Most Ordered!',
            ja: '人気No.1!',
            zh: '人气推荐!',
          }[selectedLanguage] || '많이 골랐어요!';
          return <span key={i} className="tb-tag-most">{label}</span>;
        }
        const langPath = selectedLanguage === 'en' ? 'english'
          : selectedLanguage === 'ja' ? 'japan'
          : selectedLanguage === 'zh' ? 'china'
          : 'korean';
        const tagType = tag === 'popular' ? 'popular' : tag === 'recommend' ? 'recommend' : 'new';
        const src = menutagImages[`${langPath}/${tagType}`];
        if (!src) return null;
        return <img key={i} className="tb-tag" src={src} alt={tag} />;
      });
  };

  const soldOutLabel = selectedLanguage === 'ko' ? '품절'
    : selectedLanguage === 'en' ? 'Sold out'
    : selectedLanguage === 'ja' ? '売切れ'
    : '售完';

  const timeLabel = (timeInfo) => {
    if (timeInfo.hours !== null && timeInfo.hours !== undefined) {
      return selectedLanguage === 'ko' ? `${timeInfo.hours}시간 후 오픈`
        : selectedLanguage === 'en' ? `Opens in ${timeInfo.hours}h`
        : selectedLanguage === 'ja' ? `${timeInfo.hours}時間後オープン`
        : `${timeInfo.hours}小时后开放`;
    }
    if (timeInfo.minutes !== null && timeInfo.minutes !== undefined) {
      return selectedLanguage === 'ko' ? `${timeInfo.minutes}분 후 오픈`
        : selectedLanguage === 'en' ? `Opens in ${timeInfo.minutes}m`
        : selectedLanguage === 'ja' ? `${timeInfo.minutes}分後オープン`
        : `${timeInfo.minutes}分钟后开放`;
    }
    return '';
  };

  const currentLangLabel = LANG_LABELS[selectedLanguage] || LANG_LABELS.ko;

  return (
    <div className="tb-root">
      <div className="tb-stage" ref={stageRef}>

        {/* ================= 사이드바 ================= */}
        <aside className="tb-sidebar">
          <div className="tb-store">
            <div className="tb-store-name">{storeName || ''}</div>
            {/* 언어별 테이블 표기는 orderPage 의 translations(tablePrefix/tableSuffix)를 그대로 사용
                ko '1 테이블' / en 'Table 1' / ja 'テーブル 1' / zh '1号桌' */}
            {tableNumber
              ? <div className="tb-table">{`${t.tablePrefix || ''}${tableNumber}${t.tableSuffix || ''}`}</div>
              : null}
          </div>
          <div className="tb-divider" />

          <nav className="tb-chips">
            {categories.map((category, index) => (
              <button
                key={category}
                className={`tb-chip ${index === activeCategoryIndex ? 'is-active' : ''}`}
                {...tapClickProps}
                onClick={() => onSelectCategory && onSelectCategory(index)}
              >
                {categoryLabel(category)}
              </button>
            ))}
          </nav>

          <div className="tb-wordmark">
            <img className="tb-wordmark-img" src={tabletLogo} alt="와우오더" />
          </div>
        </aside>

        {/* ================= 콘텐츠 ================= */}
        <section className="tb-content">
          <header className="tb-topbar">
            <div className="tb-title-wrap">
              <h1 className="tb-title">{currentCategory ? categoryLabel(currentCategory) : ''}</h1>
              <span className="tb-count">{itemCountLabel}</span>
            </div>

            <div className="tb-lang">
              <button
                className="tb-lang-btn"
                {...tapClickProps}
                onClick={() => setIsLangOpen(v => !v)}
              >
                <Icon icon="mdi:web" className="tb-lang-globe" />
                <span className="tb-lang-text">{currentLangLabel}</span>
                <Icon icon="mdi:chevron-down" className={`tb-lang-chev ${isLangOpen ? 'is-open' : ''}`} />
              </button>
              {isLangOpen && (
                <ul className="tb-lang-menu">
                  {languages.map(lang => (
                    <li key={lang.code}>
                      <button
                        className={`tb-lang-item ${lang.code === selectedLanguage ? 'is-active' : ''}`}
                        {...tapClickProps}
                        onClick={() => {
                          if (onSelectLanguage) onSelectLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                      >
                        {LANG_LABELS[lang.code] || lang.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </header>

          <div className="tb-list-area">
            <div className="tb-list" ref={listRef} onScroll={updateThumb}>
            <div className="tb-grid">
              {items.map((item, index) => {
                const timeInfo = calculateTimeRemaining ? calculateTimeRemaining(item) : null;
                const isSoldOut = (item.soldOut || item.is_soldout) && !timeInfo;
                const image = item.image_url || item.image;
                const name = translatedMenus[item.itemCode]?.name
                  || item[`name_${selectedLanguage}`]
                  || item.name;
                const description = translatedMenus[item.itemCode]?.description
                  || item[`description_${selectedLanguage}`]
                  || item.description
                  || '';

                return (
                  <article
                    key={item.itemCode || item.id || index}
                    className={`tb-card ${isSoldOut || timeInfo ? 'is-disabled' : ''}`}
                    onClick={() => {
                      if (isSoldOut || timeInfo) return;
                      if (onMenuClick) onMenuClick(item);
                    }}
                  >
                    <div className={`tb-card-photo ${!image ? 'is-empty' : ''}`}>
                      {image && (
                        <img
                          className="tb-card-img"
                          src={image}
                          alt={name}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <div className="tb-badges">{renderTags(item)}</div>

                      {isSoldOut && (
                        <div className="tb-overlay">
                          <div className="tb-overlay-badge">{soldOutLabel}</div>
                        </div>
                      )}
                      {timeInfo && (
                        <div className="tb-overlay">
                          <div className="tb-overlay-badge tb-time">{timeLabel(timeInfo)}</div>
                        </div>
                      )}

                      {/* 태블릿에서는 카드 전체를 눌러 옵션 화면으로 가므로 빠른담기(+) 버튼을 두지 않는다 */}
                    </div>

                    <div className="tb-card-info">
                      <div className="tb-card-name">{name}</div>
                      {description ? <div className="tb-card-desc">{description}</div> : null}
                      <div className="tb-card-price">{formatPrice(item)}</div>
                    </div>
                  </article>
                );
              })}
            </div>
            </div>
            {/* 시안 스크롤바: 폭 6px, 우측 18px 안쪽, 상하 32px 여백 */}
            {thumb.visible && (
              <div className="tb-scrolltrack">
                <div
                  className="tb-scrollthumb"
                  style={{ top: `${thumb.top}px`, height: `${thumb.height}px` }}
                />
              </div>
            )}
          </div>

          <footer className="tb-bottombar">
            <div className="tb-actions">
              <button className="tb-action" {...tapClickProps} onClick={() => onOpenStaffCall && onOpenStaffCall()}>
                <StaffCallIcon />
                <span>{selectedLanguage === 'ko' ? '직원 부르기'
                  : selectedLanguage === 'en' ? 'Call staff'
                  : selectedLanguage === 'ja' ? 'スタッフ呼出'
                  : '呼叫服务员'}</span>
              </button>
              <button className="tb-action" {...tapClickProps} onClick={() => onOpenOrderHistory && onOpenOrderHistory()}>
                <OrderHistoryIcon />
                <span>{selectedLanguage === 'ko' ? '주문 내역'
                  : selectedLanguage === 'en' ? 'Orders'
                  : selectedLanguage === 'ja' ? '注文履歴'
                  : '订单记录'}</span>
              </button>
            </div>

            <button className="tb-cart" {...tapClickProps} onClick={() => onOpenCart && onOpenCart()}>
              <span className="tb-cart-count">{cartCount}</span>
              <span className="tb-cart-label">
                {selectedLanguage === 'ko' ? '장바구니'
                  : selectedLanguage === 'en' ? 'Cart'
                  : selectedLanguage === 'ja' ? 'カート'
                  : '购物车'}
              </span>
              <span className="tb-cart-sep" />
              <span className="tb-cart-total">
                {selectedLanguage === 'ko'
                  ? `${(cartTotal || 0).toLocaleString()}원`
                  : `₩${(cartTotal || 0).toLocaleString()}`}
              </span>
            </button>
          </footer>
        </section>

        {/* toastMessage 는 orderPage 에서 SVG 아이콘이 섞인 HTML 문자열로 만들어진다 */}
        {isToastVisible && toastMessage ? (
          <div className="tb-toast" dangerouslySetInnerHTML={{ __html: toastMessage }} />
        ) : null}
      </div>

      <style>{`
        /* 장바구니 수량 배지 전용 서체 (태블릿 뷰가 떠 있을 때만 내려받는다) */
        @font-face {
          font-family: 'Paperlogy';
          src: url('https://cdn.jsdelivr.net/gh/projectnoonnu/2408-3@1.0/Paperlogy-9Black.woff2') format('woff2');
          font-weight: 900;
          font-style: normal;
          font-display: swap;
        }

        /* ===== 스테이지 ===== */
        .tb-root {
          position: fixed;
          inset: 0;
          overflow: hidden;
          background: #F7F4ED;
        }
        .tb-stage {
          width: ${ARTBOARD_WIDTH}px;
          display: flex;
          overflow: hidden;
          position: relative;
        }

        /* ===== 사이드바 (피그마 400px) ===== */
        .tb-sidebar {
          flex: 0 0 400px;
          /* 시안: 선형 그라데이션 #1E1C18 → #0E0D0B */
          background: linear-gradient(180deg, #1E1C18 0%, #0E0D0B 100%);
          display: flex;
          flex-direction: column;
          padding: 0 26px;
          border-radius: 0 28px 28px 0;
        }
        .tb-store { padding: 50px 10px 0; }
        .tb-store-name {
          font-size: 34px;
          font-weight: 900;
          color: #FFFFFF;
          line-height: 125%;
          letter-spacing: -0.035em;
        }
        .tb-table {
          margin-top: 14px;
          font-size: 26px;
          font-weight: 900;
          color: #9A9A9A;
          line-height: 125%;
          letter-spacing: -0.03em;
        }
        .tb-divider {
          height: 1px;
          /* 시안: #A88A46 75% → 5% 로 우측으로 갈수록 흐려지는 선형 그라데이션 */
          background: linear-gradient(90deg, rgba(168, 138, 70, 0.75) 0%, rgba(168, 138, 70, 0.05) 100%);
          margin: 32px 0 0;
        }

        .tb-chips {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 28px 0 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          scrollbar-width: none;
        }
        .tb-chips::-webkit-scrollbar { display: none; }
        .tb-chip {
          flex: 0 0 100px;
          width: 348px;
          border: 1.5px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          background: #2A2A2A;
          /* 비활성: 700 / 34px / 행간 125% / 자간 -3.5% / 흰색 90% */
          color: rgba(255, 255, 255, 0.9);
          font-size: 34px;
          font-weight: 700;
          line-height: 125%;
          letter-spacing: -0.035em;
          text-align: left;
          padding: 0 26px;
          cursor: pointer;
          font-family: inherit;
        }
        .tb-chip.is-active {
          background: #fdcd27;
          /* 활성: 900 / 36px / 행간 125% / 자간 -3.5% */
          color: #14120F;
          font-size: 36px;
          font-weight: 900;
          /* 시안 그림자: X0 Y6 / blur18 / spread -4 / #FCCC26 24% */
          box-shadow: 0 6px 18px -4px rgba(252, 204, 38, 0.24);
        }

        .tb-wordmark {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px 10px 40px;
        }
        /* 시안 워드마크 프레임 196x38 */
        .tb-wordmark-img {
          width: 196px;
          height: 38px;
          object-fit: contain;
          display: block;
        }

        /* ===== 콘텐츠 ===== */
        .tb-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          background: #FFFFFF;
        }

        .tb-topbar {
          flex: 0 0 132px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          /* 시안: 132px = 상단 32 + 언어버튼 68 + 하단 32 */
          padding: 32px 44px;
          border-bottom: 1.5px solid #E6DFD2;
        }
        .tb-title-wrap { display: flex; align-items: baseline; gap: 16px; }
        .tb-title {
          font-size: 50px;
          font-weight: 900;
          color: #171614;
          margin: 0;
          line-height: 125%;
          letter-spacing: -0.04em;
        }
        .tb-count {
          font-size: 26px;
          font-weight: 500;
          color: #9A9A9A;
          line-height: 125%;
          letter-spacing: -0.03em;
        }

        .tb-lang { position: relative; }
        .tb-lang-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          height: 68px;
          padding: 0 24px;
          border: none;
          border-radius: 18px;
          background: #F5F2EB;
          /* 시안: 28px / 700 / 행간 125% / 자간 -3% / #4A4640 */
          color: #4A4640;
          font-size: 28px;
          font-weight: 700;
          line-height: 125%;
          letter-spacing: -0.03em;
          cursor: pointer;
          font-family: inherit;
        }
        .tb-lang-globe { font-size: 28px; color: #6B6B6B; }
        .tb-lang-chev { font-size: 24px; color: #9A9A9A; transition: transform .18s ease; }
        .tb-lang-chev.is-open { transform: rotate(180deg); }
        .tb-lang-menu {
          position: absolute;
          top: 78px;
          right: 0;
          min-width: 194px;
          list-style: none;
          margin: 0;
          padding: 8px;
          background: #FFFFFF;
          border: 1px solid #E4DFD4;
          border-radius: 18px;
          box-shadow: 0 12px 32px rgba(0,0,0,.12);
          z-index: 20;
        }
        .tb-lang-item {
          width: 100%;
          border: none;
          background: transparent;
          padding: 14px 18px;
          border-radius: 12px;
          font-size: 24px;
          font-weight: 600;
          color: #191919;
          text-align: left;
          cursor: pointer;
          font-family: inherit;
        }
        .tb-lang-item.is-active { background: #FFF6D6; color: #191919; }

        /* ===== 메뉴 목록 ===== */
        .tb-list-area {
          position: relative;
          flex: 1;
          min-height: 0;
          display: flex;
        }
        .tb-list {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          background: #f2efe9;
          /* 네이티브 스크롤바는 숨기고 아래 커스텀 인디케이터로 대체 */
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .tb-list::-webkit-scrollbar { width: 0; height: 0; display: none; }

        .tb-scrolltrack {
          position: absolute;
          right: 18px;
          top: 32px;
          bottom: 32px;
          width: 6px;
          border-radius: 3px;
          background: rgba(23, 22, 20, 0.06);
          pointer-events: none;
        }
        .tb-scrollthumb {
          position: absolute;
          left: 0;
          width: 6px;
          border-radius: 3px;
          background: rgba(23, 22, 20, 0.22);
        }

        .tb-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          column-gap: 28px;
          row-gap: 26px;
          padding: 32px 44px 44px;
        }

        .tb-card {
          background: #FFFFFF;
          border-radius: 20px;
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          /* 시안 테두리: 1px 모든 면 / #171614 6% (내부 정렬) */
          border: 1px solid rgba(23, 22, 20, 0.06);
          /* 시안 그림자: X0 Y8 / blur24 / spread -8 / #1A170F 10% */
          box-shadow: 0 8px 24px -8px rgba(26, 23, 15, 0.10);
        }
        .tb-card.is-disabled { cursor: default; }

        .tb-card-photo {
          position: relative;
          width: 100%;
          aspect-ratio: 485 / 320;
          background: #EFEBE2;
          overflow: hidden;
        }
        .tb-card-img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .tb-badges {
          position: absolute;
          top: 18px;
          left: 18px;
          display: flex;
          gap: 8px;
          align-items: center;
        }
        .tb-tag { height: 44px; width: auto; display: block; }
        .tb-tag-most {
          background: #191919;
          color: #FFFFFF;
          font-size: 20px;
          font-weight: 700;
          padding: 8px 14px;
          border-radius: 999px;
        }

        .tb-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,.45);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .tb-overlay-badge {
          color: #FFFFFF;
          font-size: 30px;
          font-weight: 800;
          padding: 12px 26px;
          border: 2px solid rgba(255,255,255,.85);
          border-radius: 999px;
        }
        .tb-overlay-badge.tb-time { font-size: 26px; }

        .tb-card-info { padding: 24px 26px 26px; }
        .tb-card-name {
          font-size: 40px;
          font-weight: 900;
          color: #171614;
          line-height: 125%;
          letter-spacing: -0.035em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .tb-card-desc {
          margin-top: 11px;
          font-size: 22px;
          font-weight: 400;
          color: #8C857A;
          line-height: 125%;
          letter-spacing: -0.03em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .tb-card-price {
          margin-top: 3px;
          text-align: right;
          font-size: 42px;
          font-weight: 900;
          color: #171614;
          line-height: 125%;
          letter-spacing: -0.035em;
        }

        /* ===== 하단바 ===== */
        .tb-bottombar {
          flex: 0 0 176px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          /* 시안: 176px = 상단 36 + 버튼 104 + 하단 36 */
          padding: 36px 44px;
          background: #FFFFFF;
          /* 목록 위로 그림자가 올라오도록 스택 순서를 명시 */
          position: relative;
          z-index: 1;
          /* 시안 그림자: X0 Y-8 / blur26 / spread0 / #17140D 12% */
          box-shadow: 0 -8px 26px 0 rgba(23, 20, 13, 0.12);
        }
        .tb-actions { display: flex; gap: 40px; }
        .tb-action {
          height: 104px;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 0 34px;
          border: none;
          border-radius: 20px;
          background: #EDE9E0;
          color: #2A2723;
          font-size: 32px;
          font-weight: 700;
          line-height: 125%;
          letter-spacing: -0.035em;
          cursor: pointer;
          font-family: inherit;
        }
        .tb-action-icon { width: 34px; height: 34px; display: block; flex: 0 0 34px; }

        .tb-cart {
          height: 104px;
          min-width: 429px;
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 0 32px;
          border: none;
          border-radius: 20px;
          background: #fdcd27;
          color: #191919;
          cursor: pointer;
          font-family: inherit;
          /* 시안 그림자: X0 Y8 / blur20 / spread -4 / #D9A80F 35% */
          box-shadow: 0 8px 20px -4px rgba(217, 168, 15, 0.35);
        }
        .tb-cart-count {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #191919;
          color: #FDCD27;
          /* 시안: Paperlogy 9 Black / 26px / 행간 125% / 자간 -2% */
          font-family: 'Paperlogy', 'Pretendard', sans-serif;
          font-size: 26px;
          font-weight: 900;
          line-height: 125%;
          letter-spacing: -0.02em;
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 48px;
        }
        .tb-cart-label {
          font-size: 32px;
          font-weight: 900;
          color: #14120F;
          line-height: 125%;
          letter-spacing: -0.035em;
        }
        .tb-cart-sep { width: 2px; height: 44px; background: rgba(25,25,25,.25); }
        .tb-cart-total {
          font-size: 34px;
          font-weight: 900;
          color: #14120F;
          line-height: 125%;
          letter-spacing: -0.035em;
          margin-left: auto;
        }

        /* ===== 토스트 ===== */
        .tb-toast {
          position: absolute;
          left: 50%;
          bottom: 220px;
          transform: translateX(-50%);
          background: rgba(25,25,25,.92);
          color: #FFFFFF;
          font-size: 26px;
          font-weight: 700;
          padding: 20px 34px;
          border-radius: 999px;
          z-index: 50;
        }

        /* ===== Noto Sans KR 적용 대상 =====
         * 개별 규칙(.tb-chip / .tb-action 의 font-family: inherit)보다 뒤에 두어 확실히 이기게 한다. */
        .tb-store-name,
        .tb-table,
        .tb-chip,
        .tb-lang-text,
        .tb-card-name,
        .tb-card-desc,
        .tb-card-price,
        .tb-action,
        .tb-cart-label,
        .tb-cart-total {
          font-family: 'Noto Sans KR', 'Pretendard', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /*
         * ===== 기존 바텀시트들의 태블릿 대응 =====
         * 각 시트는 scaleUI 로 zoom:1.5 를 "인라인"으로 쓰는데, zoom 은 세로축까지 확대하므로
         * height:100dvh 인 시트가 뷰포트보다 커져 주문 버튼이 화면 밖으로 나간다.
         * (reviewBenefits.jsx 가 같은 증상을 zoom:1 로 해결한 선례가 있다)
         * 인라인 zoom 을 이기려면 !important 가 필요하다.
         * reviewBenefits 는 자체 해결돼 있으므로 제외.
         */
        body.tablet-layout .cart-drawer-wrapper,
        body.tablet-layout .bottom-sheet-wrapper,
        body.tablet-layout .order-history-wrapper,
        body.tablet-layout .staff-call-wrapper,
        body.tablet-layout .order-completed-wrapper,
        body.tablet-layout .phone-input-wrapper,
        body.tablet-layout .review-guide-wrapper {
          zoom: 1 !important;
          width: 360px !important;
          margin: 0 auto !important;
        }
      `}</style>
    </div>
  );
};

export default TabletOrderView;
